import {
  CdkDrag,
  CdkDragDrop,
  CdkDragHandle,
  CdkDropList,
  moveItemInArray,
} from '@angular/cdk/drag-drop';
import { NgOptimizedImage } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormArray, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';

import { type Observable, of, take } from 'rxjs';

import { injectTranslate, TranslatePipe } from '@core/i18n-legacy';
import { TellentPositionsDataFacade } from '@core/tellent-positions-logic';
import { injectProficiencyTierLabels, SkillsIdeateDataFacade } from '@core/tellent-skills-logic';
import {
  PROFICIENCY_TIER_PRESET_FIVE,
  PROFICIENCY_TIER_PRESET_FOUR,
  type SkillsIdeateFlowData,
} from '@core/tellent-skills-types';
import {
  ButtonComponent,
  CheckboxComponent,
  ChipsComponent,
  DialogBodyComponent,
  DialogComponentI,
  DialogFooterComponent,
  DialogHeaderComponent,
  DialogRef,
  DialogService,
  DropdownItemComponent,
  HeaderDefaultComponent,
  HeadingComponent,
  IconButtonComponent,
  IconComponent,
  InputComponent,
  MultiselectFeatureDirective,
  MultiSelectTriggerComponent,
  NotificationsController,
  SearchFeatureDirective,
  Select2Component,
  type Select2Item,
  Select2ListItemDirective,
  Select2SelectedItemDirective,
  Select2TriggerComponent,
  SelectedItemCounterDirective,
  TextareaComponent,
} from '@core/user-interface-legacy';

type ProficiencyPreset = 'four' | 'five' | 'custom';

const ALL_POSITIONS = '__all_positions__';
const MAX_INSTRUCTIONS_LENGTH = 1000;
const MIN_CUSTOM_LEVELS = 2;
const INITIAL_CUSTOM_LEVELS = 3;

@Component({
  selector: 'tlc-skills-ideate-options-dialog',
  templateUrl: './skills-ideate-options-dialog.component.html',
  styleUrl: './skills-ideate-options-dialog.component.less',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CdkDrag,
    CdkDragHandle,
    CdkDropList,
    ButtonComponent,
    CheckboxComponent,
    ChipsComponent,
    DialogBodyComponent,
    DialogFooterComponent,
    DialogHeaderComponent,
    DropdownItemComponent,
    HeaderDefaultComponent,
    HeadingComponent,
    IconButtonComponent,
    IconComponent,
    InputComponent,
    MultiselectFeatureDirective,
    MultiSelectTriggerComponent,
    NgOptimizedImage,
    ReactiveFormsModule,
    SearchFeatureDirective,
    Select2Component,
    Select2ListItemDirective,
    Select2SelectedItemDirective,
    Select2TriggerComponent,
    SelectedItemCounterDirective,
    TextareaComponent,
    TranslatePipe,
  ],
})
export class SkillsIdeateOptionsDialogComponent implements DialogComponentI<
  void,
  Partial<SkillsIdeateFlowData>
> {
  private readonly t = injectTranslate();
  private readonly dialogRef = inject(DialogRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly notificationsController = inject(NotificationsController);
  private readonly positionsFacade = inject(TellentPositionsDataFacade);
  private readonly skillsIdeateDataFacade = inject(SkillsIdeateDataFacade);
  private readonly dialogService = inject(DialogService);
  private readonly proficiencyTierLabels = injectProficiencyTierLabels();

  protected readonly ALL_POSITIONS = ALL_POSITIONS;
  protected readonly maxInstructionsLength = MAX_INSTRUCTIONS_LENGTH;

  protected readonly isGenerating = signal(false);
  protected readonly hasFormChanges = signal(false);

  protected readonly positionsData = this.positionsFacade.positionsData;

  protected readonly positionItems = computed<Array<Select2Item<string>>>(() => [
    {
      value: ALL_POSITIONS,
      label: this.t('tlc.positions.skills_ideate_flow.options.positions.all'),
    },
    ...this.positionsData().map(position => ({ value: position.guid, label: position.name })),
  ]);

  protected readonly membersCountByGuid = computed(
    () => new Map(this.positionsData().map(position => [position.guid, position.membersCount])),
  );

  protected readonly totalMembersCount = computed(() =>
    this.positionsData().reduce((total, position) => total + position.membersCount, 0),
  );

  protected readonly proficiencyPresetItems: Array<Select2Item<ProficiencyPreset>> = [
    {
      value: 'four',
      label: this.t('tlc.positions.skills_ideate_flow.options.proficiency.four.label'),
    },
    {
      value: 'five',
      label: this.t('tlc.positions.skills_ideate_flow.options.proficiency.five.label'),
    },
    {
      value: 'custom',
      label: this.t('tlc.positions.skills_ideate_flow.options.proficiency.custom.label'),
    },
  ];

  protected readonly proficiencyPresetDescriptions: Record<ProficiencyPreset, string> = {
    four: PROFICIENCY_TIER_PRESET_FOUR.map(tier => this.proficiencyTierLabels[tier]).join(', '),
    five: PROFICIENCY_TIER_PRESET_FIVE.map(tier => this.proficiencyTierLabels[tier]).join(', '),
    custom: this.t('tlc.positions.skills_ideate_flow.options.proficiency.custom.description'),
  };

  protected readonly form = new FormGroup({
    positionGuids: new FormControl<string[]>([ALL_POSITIONS], { nonNullable: true }),
    proficiencyPreset: new FormControl<ProficiencyPreset>('four', { nonNullable: true }),
    customLevels: new FormArray<FormControl<string>>(
      Array.from(
        { length: INITIAL_CUSTOM_LEVELS },
        () => new FormControl<string>('', { nonNullable: true }),
      ),
    ),
    additionalInstructions: new FormControl('', {
      nonNullable: true,
      validators: Validators.maxLength(MAX_INSTRUCTIONS_LENGTH),
    }),
  });

  protected readonly positionGuidsValue = toSignal(this.form.controls.positionGuids.valueChanges, {
    initialValue: this.form.controls.positionGuids.value,
  });
  protected readonly presetValue = toSignal(this.form.controls.proficiencyPreset.valueChanges, {
    initialValue: this.form.controls.proficiencyPreset.value,
  });
  protected readonly customLevelsValue = toSignal(this.form.controls.customLevels.valueChanges, {
    initialValue: this.form.controls.customLevels.getRawValue(),
  });
  protected readonly instructionsStatus = toSignal(
    this.form.controls.additionalInstructions.statusChanges,
    { initialValue: this.form.controls.additionalInstructions.status },
  );

  protected readonly isCustomPreset = computed(() => this.presetValue() === 'custom');

  protected readonly effectivePositionGuids = computed<string[]>(() => {
    const selected = this.positionGuidsValue();
    if (selected.includes(ALL_POSITIONS)) {
      return this.positionsData().map(position => position.guid);
    }
    return selected;
  });

  protected readonly validCustomLevels = computed<string[]>(() =>
    this.customLevelsValue()
      .map(name => name.trim())
      .filter(name => name.length > 0),
  );

  protected readonly isGenerateDisabled = computed(() => {
    if (this.isGenerating()) return true;
    if (this.instructionsStatus() === 'INVALID') return true;
    if (this.effectivePositionGuids().length === 0) return true;
    if (this.presetValue() === 'custom' && this.validCustomLevels().length < MIN_CUSTOM_LEVELS) {
      return true;
    }
    return false;
  });

  constructor() {
    this.positionsFacade.fetchPositions();
    this.form.valueChanges
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.hasFormChanges.set(true));
  }

  protected get customLevels(): FormArray<FormControl<string>> {
    return this.form.controls.customLevels;
  }

  public setDialogData(_data: void, _triggerUpdate: boolean): void {
    this.dialogRef.containerInstance.dialogConfig = {
      ...this.dialogRef.containerInstance.dialogConfig,
      closeGuard: () =>
        this.hasFormChanges() && !this.isGenerating() ? this.confirmLeave() : of(true),
    };
  }

  public cancel(): void {
    this.dialogRef.close();
  }

  public generate(): void {
    if (this.isGenerateDisabled()) return;

    this.isGenerating.set(true);

    this.skillsIdeateDataFacade
      .startIdeate({
        positionGuids: this.effectivePositionGuids(),
        proficiencyTierNames: this.proficiencyTierNames(),
        userPrompt: this.buildUserPrompt(),
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ideate => {
          this.dialogRef.close({ id: ideate.id, resultJson: null, error: null });
        },
        error: () => {
          this.isGenerating.set(false);
          this.notificationsController.error({
            title: this.t('tlc.common.errors.unknown_error'),
          });
        },
      });
  }

  protected addCustomLevel(): void {
    this.customLevels.push(new FormControl<string>('', { nonNullable: true }));
  }

  protected removeCustomLevel(index: number): void {
    if (this.customLevels.length <= 1) return;
    this.customLevels.removeAt(index);
  }

  protected reorderCustomLevels(event: CdkDragDrop<unknown>): void {
    const controls = this.customLevels.controls;
    moveItemInArray(controls, event.previousIndex, event.currentIndex);
    this.customLevels.updateValueAndValidity();
  }

  protected presetDescription(preset: ProficiencyPreset): string {
    return this.proficiencyPresetDescriptions[preset];
  }

  protected isPositionChecked(value: string): boolean {
    const selected = this.form.controls.positionGuids.value;
    if (selected.includes(ALL_POSITIONS)) return true;
    return selected.includes(value);
  }

  protected togglePosition(value: string): void {
    const allGuids = this.positionsData().map(position => position.guid);
    const current = this.form.controls.positionGuids.value;
    const allSelected = current.includes(ALL_POSITIONS);

    if (value === ALL_POSITIONS) {
      this.form.controls.positionGuids.setValue(allSelected ? [] : [ALL_POSITIONS]);
      return;
    }

    const effective = allSelected ? allGuids : current;
    const toggled = effective.includes(value)
      ? effective.filter(guid => guid !== value)
      : [...effective, value];

    let next: string[];
    if (toggled.length === 0) {
      next = [];
    } else if (allGuids.length > 0 && allGuids.every(guid => toggled.includes(guid))) {
      next = [ALL_POSITIONS];
    } else {
      next = toggled;
    }

    this.form.controls.positionGuids.setValue(next);
  }

  private proficiencyTierNames(): string[] {
    switch (this.presetValue()) {
      case 'five':
        return PROFICIENCY_TIER_PRESET_FIVE.map(tier => this.proficiencyTierLabels[tier]);
      case 'custom':
        return this.validCustomLevels();
      case 'four':
      default:
        return PROFICIENCY_TIER_PRESET_FOUR.map(tier => this.proficiencyTierLabels[tier]);
    }
  }

  private buildUserPrompt(): string | null {
    const instructions = this.form.controls.additionalInstructions.value.trim();
    return instructions.length > 0 ? instructions : null;
  }

  private confirmLeave(): Observable<boolean> {
    return this.dialogService.error({
      title: this.t('tlc.positions.skills_ideate_flow.leave_confirmation.options.title'),
      message: this.t('tlc.positions.skills_ideate_flow.leave_confirmation.options.message'),
      agreeLabel: this.t('tlc.positions.skills_ideate_flow.leave_confirmation.confirm'),
      isCancelable: true,
    });
  }
}
